import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import brandPortalAccountRoute from "@faire/web-api--source/routes/brand-portal/account";
import brandPortalAnalytics from "@faire/web-api--source/routes/brand-portal/analytics";
import brandPortalCustomerServiceAnalytics from "@faire/web-api--source/routes/brand-portal/analytics/customer-service";
import brandPortalReviews from "@faire/web-api--source/routes/brand-portal/analytics/reviews";
import brandPortalConfirmIdentityBrandIdvToken from "@faire/web-api--source/routes/brand-portal/confirm-identity/brandIDVToken";
import brandPortalIntegrations from "@faire/web-api--source/routes/brand-portal/integrations";
import brandPortalMarketingInsiderPartnership from "@faire/web-api--source/routes/brand-portal/marketing/insider-partnership";
import brandPortalSamples from "@faire/web-api--source/routes/brand-portal/marketing/samples";
import brandPortalProducts from "@faire/web-api--source/routes/brand-portal/my-shop/products";
import brandPortalMyShopSettings from "@faire/web-api--source/routes/brand-portal/my-shop/settings";
import brandPortalMyShop from "@faire/web-api--source/routes/brand-portal/my-shop/story";
import brandPortalOrders from "@faire/web-api--source/routes/brand-portal/orders";
import brandPortalOrdersbrandOrderToken from "@faire/web-api--source/routes/brand-portal/orders/brandOrderToken";
import { path as getBrandOrderFulfillmentRoute } from "@faire/web-api--source/routes/brand-portal/orders/brandOrderToken/order-fulfilment";
import brandPortalOrdersbrandOrderTokenOrderInvoice from "@faire/web-api--source/routes/brand-portal/orders/brandOrderToken/order-invoice";
import { path as requestCommissionUpdatePath } from "@faire/web-api--source/routes/brand-portal/orders/brandOrderToken/request-commission-update";
import brandPortalPayouts from "@faire/web-api--source/routes/brand-portal/payouts";
import supportBrands from "@faire/web-api--source/routes/support/brands";

export const GoToOrderFulfillmentCTA = (
  brandOrderToken: string,
  strictLocalize: StrictLocalizeFunction
) => ({
  label: strictLocalize({
    defaultMessage: "Go to Order Fulfillment",
    description: {
      text: "CTA button to direct the user to the order fulfillment page",
    },
  }),
  getLink: () => {
    return getBrandOrderFulfillmentRoute(brandOrderToken);
  },
});

export const ViewOrderDetailsInFulfillmentCTA = (
  brandOrderToken: string,
  strictLocalize: StrictLocalizeFunction
) => ({
  label: strictLocalize({
    defaultMessage: "View order details",
    description: {
      text: "CTA button to direct the user to the order fulfillment page",
    },
  }),
  getLink: () => {
    return getBrandOrderFulfillmentRoute(brandOrderToken);
  },
});

export const GoToAccountSettingsCTA = (
  strictLocalize: StrictLocalizeFunction
) => {
  return {
    label: strictLocalize({
      defaultMessage: "Go to Account Settings",
      description: {
        text: "CTA button to go to the account settings page",
      },
    }),
    getLink: () => brandPortalAccountRoute({ tab: "my-account" }),
  };
};

export const GoToAnalyticsCTA = (strictLocalize: StrictLocalizeFunction) => {
  return {
    label: strictLocalize({
      defaultMessage: "Go to Analytics",
      description: {
        text: "CTA button to go to the analytics page",
      },
    }),
    getLink: () => brandPortalAnalytics(),
  };
};

export const ViewCustomerServiceAnalyticsCTA = (
  strictLocalize: StrictLocalizeFunction
) => {
  return {
    label: strictLocalize({
      defaultMessage: "View Customer Service Analytics",
      description: {
        text: "CTA button to go to the customer service analytics page",
      },
    }),
    getLink: () => brandPortalCustomerServiceAnalytics(),
  };
};

export const GoToReviewsCTA = (strictLocalize: StrictLocalizeFunction) => {
  return {
    label: strictLocalize({
      defaultMessage: "Go to Reviews",
      description: {
        text: "CTA button to go to the reviews page",
      },
    }),
    getLink: () => brandPortalReviews(),
  };
};

export const GoToShopPageCTA = (strictLocalize: StrictLocalizeFunction) => {
  return {
    label: strictLocalize({
      defaultMessage: "Go to Shop page",
      description: {
        text: "CTA button to go to the my shop page",
      },
    }),
    getLink: () => brandPortalMyShop(),
  };
};

export const GoToShopSettingsCTA = (strictLocalize: StrictLocalizeFunction) => {
  return {
    label: strictLocalize({
      defaultMessage: "Go to Shop settings",
      description: {
        text: "CTA button to go to the shop settings page",
      },
    }),
    getLink: () => brandPortalMyShopSettings(),
  };
};

export const GoToProductsCTA = (strictLocalize: StrictLocalizeFunction) => {
  return {
    label: strictLocalize({
      defaultMessage: "Go to Products",
      description: {
        text: "CTA button to go to the products page",
      },
    }),
    getLink: () => brandPortalProducts(),
  };
};

export const GoToSamplesCTA = (strictLocalize: StrictLocalizeFunction) => {
  return {
    label: strictLocalize({
      defaultMessage: "Go to Samples",
      description: {
        text: "CTA button to go to the samples page",
      },
    }),
    getLink: () => brandPortalSamples(),
  };
};

export const GoToIntegrationsCTA = (strictLocalize: StrictLocalizeFunction) => {
  return {
    label: strictLocalize({
      defaultMessage: "Go to Integrations",
      description: {
        text: "CTA button to go to the integrations page",
      },
    }),
    getLink: () => brandPortalIntegrations(),
  };
};

export const SubmitVerificationInformationCTA = (
  brandIdvToken: string,
  strictLocalize: StrictLocalizeFunction
) => {
  return {
    label: strictLocalize({
      defaultMessage: "Submit information",
      description: {
        text: "CTA button to submit additional information for account verification",
      },
    }),
    getLink: () => {
      return brandPortalConfirmIdentityBrandIdvToken(brandIdvToken);
    },
  };
};

export const GoToBillingCTA = (strictLocalize: StrictLocalizeFunction) => {
  return {
    label: strictLocalize({
      defaultMessage: "Go to Billing Settings",
      description: {
        text: "CTA button to go to the billing page in the account settings",
      },
    }),
    getLink: () => brandPortalAccountRoute({ tab: "billing" }),
  };
};

export const ViewPayoutsCTA = (strictLocalize: StrictLocalizeFunction) => {
  return {
    label: strictLocalize({
      defaultMessage: "View payouts",
      description: {
        text: "CTA button to view the payouts in the account settings",
      },
    }),
    getLink: () => brandPortalPayouts(),
  };
};

export const ViewOrdersCTA = (strictLocalize: StrictLocalizeFunction) => {
  return {
    label: strictLocalize({
      defaultMessage: "View orders",
      description: {
        text: "CTA button to view the orders in the account settings",
      },
    }),
    getLink: () => brandPortalOrders(),
  };
};

export const BecomeInsiderCTA = (strictLocalize: StrictLocalizeFunction) => {
  return {
    label: strictLocalize({
      defaultMessage: "Become an Insider Partner",
      description: {
        text: "CTA button to go to the insider partnership page",
      },
    }),
    getLink: () => brandPortalMarketingInsiderPartnership(),
  };
};

export const GoToPayoutSettingsCTA = (
  strictLocalize: StrictLocalizeFunction
) => {
  return {
    label: strictLocalize({
      defaultMessage: "Go to Payout Settings",
      description: {
        text: "CTA button to go to the payout settings page",
      },
    }),
    getLink: () => brandPortalAccountRoute({ tab: "payment" }),
  };
};

export const GoToNotificationSettingsCTA = (
  strictLocalize: StrictLocalizeFunction
) => {
  return {
    label: strictLocalize({
      defaultMessage: "Go to Notification Settings",
      description: {
        text: "CTA button to go to the notification settings page",
      },
    }),
    getLink: () => brandPortalAccountRoute({ tab: "notifications" }),
  };
};

export const GoToHelpCenterCTA = (strictLocalize: StrictLocalizeFunction) => {
  return {
    label: strictLocalize({
      defaultMessage: "Go to Help Center",
      description: {
        text: "CTA button to go to the help center brand page",
      },
    }),
    getLink: () => supportBrands(),
  };
};

export const GoToOrdersCTA = (strictLocalize: StrictLocalizeFunction) => {
  return {
    label: strictLocalize({
      defaultMessage: "Go to Orders",
      description: {
        text: "CTA button to go to the brand orders page",
      },
    }),
    getLink: () => brandPortalOrders(),
  };
};

export const ViewInvoiceCTA = (
  brandOrderToken: string,
  strictLocalize: StrictLocalizeFunction
) => {
  return {
    label: strictLocalize({
      defaultMessage: "View invoice",
      description: {
        text: "CTA button to view the invoice of an order",
      },
    }),
    getLink: () =>
      brandPortalOrdersbrandOrderTokenOrderInvoice(brandOrderToken),
  };
};

export const ViewOrderDetailsCTA = (
  brandOrderToken: string,
  strictLocalize: StrictLocalizeFunction
) => {
  return {
    label: strictLocalize({
      defaultMessage: "View order details",
      description: {
        text: "CTA button to view the details of an order",
      },
    }),
    getLink: () => brandPortalOrdersbrandOrderToken(brandOrderToken),
  };
};

export const RequestCommissionChangeCTA = (
  brandOrderToken: string,
  strictLocalize: StrictLocalizeFunction
) => {
  return {
    label: strictLocalize({
      defaultMessage: "Request commission change",
      description: {
        text: "CTA button to go to the request commission change flow",
        img: "https://cdn.faire.com/fastly/e61ab18bcc625384ed6e1385c3c3523e67908ac8e9346eff73667140ac07bc4b.png",
      },
    }),
    getLink: () => requestCommissionUpdatePath(brandOrderToken),
  };
};

export const UpdateTaxInformationCTA = (
  strictLocalize: StrictLocalizeFunction
) => {
  return {
    label: strictLocalize({
      defaultMessage: "Update tax information",
      description: {
        text: "CTA button to go to the tax information page",
      },
    }),
    getLink: () => brandPortalAccountRoute({ tab: "export-and-taxes" }),
  };
};
